import React from 'react';
import { motion } from 'framer-motion';
import { getGrowthStage } from '../../mastery';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { useDjaySelection } from '../../mascot/useDjaySelection';
import { DjayAvatar, DjayVariant, DjayEmotion, DjayTier, VARIANT_COLORS } from '../../mascot/DjayAvatar';
/**
 * DjayGrowthState — Djay-based learner mastery renderer.
 *
 * Drop-in replacement for GrowthStageIcon: SAME prop signature
 * (masteryScore / size / className) so the known call-sites swap cleanly.
 *
 * The growth states are now rendered as the REAL `DjayAvatar` (correct
 * flame-tuft body, signature shades, tail, feet) so every level is
 * unmistakably Meester Djay. Each level maps to a distinct on-brand
 * emotion/tier persona, with growth-only flourishes layered on top:
 *
 *   L0 Nieuw        → sleeping Djay  + "zzz", slightly dimmed
 *   L1 Net begonnen → thinking Djay  + curious spark
 *   L2 Groeit       → happy Djay     (steady, finding his groove)
 *   L3 Goed op weg  → encouraging Djay + rhythm notes
 *   L4 Sterk        → excited Djay   + tier glow ("in the zone")
 *   L5 Meester!     → proud Djay, master tier (crown+cape) + golden glow
 *
 *  • D2 — the learner's equipped variant tints Djay automatically via the
 *    DjayAvatar `variant` prop.
 *  • D3 — ghosting: opacity from the optional decay props, floored so Djay
 *    never vanishes, snapping back to full on re-practice, with a tooltip.
 *  • Motion respects `useMotionBudget`. `getGrowthStage` is untouched.
 */
interface DjayGrowthStateProps {
  masteryScore?: number;
  /**
   * Optional explicit growth level (0-5) override. When provided it bypasses
   * `getGrowthStage(masteryScore)` — used by callers (e.g. session-summary
   * stage-up moments) that already know the exact level to show.
   */
  level?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /** Optional ghosting input — caller computes via masteryDecay.getDecayStatus(state) */
  decayStatus?: 'fresh' | 'recent' | 'fading' | 'stale';
  /** Optional ghosting input (0-100) — caller computes via masteryDecay.getDecayPercentage(state) */
  decayPercentage?: number;
}
const GHOST_HINT = 'Djay wordt een beetje doorzichtig — je hebt dit even niet geoefend. Oefen even en hij komt weer helemaal tevoorschijn!';
/** A score squarely inside each growth band, so an explicit `level` override
 *  resolves back to that exact level through `getGrowthStage`. */
const LEVEL_TO_REPRESENTATIVE_SCORE: Record<number, number> = {
  0: 0,
  1: 12,
  2: 37,
  3: 65,
  4: 87,
  5: 100
};
interface LevelPersona {
  emotion: DjayEmotion;
  tier: DjayTier;
  /** Soft halo behind Djay (level 4+) */
  glow?: 'none' | 'tier' | 'gold';
  /** Slight dim for the dormant level */
  dim?: boolean;
}
const LEVEL_PERSONA: Record<number, LevelPersona> = {
  0: {
    emotion: 'sleeping',
    tier: 'rookie',
    glow: 'none',
    dim: true
  },
  1: {
    emotion: 'thinking',
    tier: 'rookie',
    glow: 'none'
  },
  2: {
    emotion: 'happy',
    tier: 'rookie',
    glow: 'none'
  },
  3: {
    emotion: 'encouraging',
    tier: 'pro',
    glow: 'none'
  },
  4: {
    emotion: 'excited',
    tier: 'pro',
    glow: 'tier'
  },
  5: {
    emotion: 'proud',
    tier: 'master',
    glow: 'gold'
  }
};
/**
 * Fresh/recent → fully present. Fading/stale → proportionally transparent,
 * floored at 0.45 so Djay is always still legible.
 */
function resolveGhostOpacity(status: DjayGrowthStateProps['decayStatus'], pct: number | undefined): number {
  if (!status || status === 'fresh' || status === 'recent') return 1;
  const decay = Math.max(0, Math.min(100, pct ?? (status === 'stale' ? 60 : 30)));
  return Math.max(0.45, 1 - decay / 100 * 0.55);
}
export function DjayGrowthState({
  masteryScore = 0,
  level,
  size = 'md',
  className = '',
  decayStatus,
  decayPercentage
}: DjayGrowthStateProps) {
  const scoreStage = getGrowthStage(masteryScore);
  // An explicit `level` override wins over the score-derived stage.
  const stage = level !== undefined ? getGrowthStage(LEVEL_TO_REPRESENTATIVE_SCORE[level] ?? masteryScore) : scoreStage;
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    selectedVariant,
    selectedAccessory
  } = useDjaySelection();
  const variant = selectedVariant as DjayVariant ?? 'default';
  const colors = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;
  const persona = LEVEL_PERSONA[stage.level] ?? LEVEL_PERSONA[0];
  const isCompact = size === 'sm';
  const ghosted = decayStatus === 'fading' || decayStatus === 'stale';
  const opacity = resolveGhostOpacity(decayStatus, decayPercentage);
  const glowColor = persona.glow === 'gold' ? '#FCD34D' : colors.bodyGradient[0];
  return <motion.div className={`relative inline-flex items-center justify-center ${className}`} title={ghosted ? `${stage.label} — ${GHOST_HINT}` : stage.label} role="img" aria-label={`${stage.label}${ghosted ? ' (even niet geoefend)' : ''}`} animate={{
    opacity
  }} transition={{
    duration: shouldAnimate ? 0.5 : 0,
    ease: 'easeOut'
  }} data-id="element-2305">
      {/* Tier / mastery glow behind Djay (level 4+). Hidden at compact size
                  to keep tiny trail nodes clean. */}
      {!isCompact && persona.glow && persona.glow !== 'none' && <motion.span aria-hidden className="absolute inset-0 rounded-full" style={{
      background: `radial-gradient(circle, ${glowColor} 0%, transparent 68%)`,
      opacity: persona.glow === 'gold' ? 0.55 : 0.4
    }} animate={shouldAnimate ? {
      scale: [1, 1.12, 1],
      opacity: persona.glow === 'gold' ? [0.4, 0.62, 0.4] : [0.28, 0.46, 0.28]
    } : undefined} transition={{
      repeat: Infinity,
      duration: persona.glow === 'gold' ? 2.6 : 2.2,
      ease: 'easeInOut'
    }} data-id="element-2306" />}

      {/* The real Djay — guaranteed on-brand silhouette, shades, flame tuft */}
      <div className="relative" style={persona.dim ? {
      filter: 'saturate(0.7) brightness(0.97)'
    } : undefined} data-id="element-2307">
        <DjayAvatar size={size} emotion={persona.emotion} tier={persona.tier} variant={variant} accessory={selectedAccessory} animate={shouldAnimate} data-id="element-2308" />
      </div>

      {/* ── Growth-only flourishes ───────────────────────────────────── */}

      {/* L0 — sleepy "zzz" */}
      {!isCompact && stage.level === 0 && <motion.svg aria-hidden viewBox="0 0 40 40" className="absolute -top-1 -right-1 w-1/3 h-1/3 pointer-events-none" animate={shouldAnimate ? {
      opacity: [0.3, 1, 0.3],
      y: [2, -3, 2]
    } : undefined} transition={{
      repeat: Infinity,
      duration: 2.6,
      ease: 'easeInOut'
    }} data-id="element-2309">
          <text x="4" y="30" fontSize="16" fontWeight="bold" fill="#94A3B8" data-id="element-2310">
            z
          </text>
          <text x="16" y="20" fontSize="12" fontWeight="bold" fill="#94A3B8" data-id="element-2311">
            z
          </text>
          <text x="26" y="12" fontSize="9" fontWeight="bold" fill="#94A3B8" data-id="element-2312">
            z
          </text>
        </motion.svg>}

      {/* L1 — curious spark */}
      {!isCompact && stage.level === 1 && <motion.svg aria-hidden viewBox="0 0 24 24" className="absolute top-0 right-0 w-1/4 h-1/4 pointer-events-none" animate={shouldAnimate ? {
      scale: [0.7, 1.1, 0.7],
      opacity: [0.6, 1, 0.6]
    } : undefined} transition={{
      repeat: Infinity,
      duration: 1.8,
      ease: 'easeInOut'
    }} style={{
      transformOrigin: 'center'
    }} data-id="element-2313">
          <path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6z" fill="#FDE047" data-id="element-2314" />
        </motion.svg>}

      {/* L3 — rhythm notes (finding the beat) */}
      {!isCompact && stage.level === 3 && <motion.svg aria-hidden viewBox="0 0 24 24" className="absolute top-0 right-0 w-1/4 h-1/4 pointer-events-none" animate={shouldAnimate ? {
      y: [0, -3, 0],
      opacity: [0.5, 1, 0.5]
    } : undefined} transition={{
      repeat: Infinity,
      duration: 1.4,
      ease: 'easeInOut'
    }} fill="#F59E0B" data-id="element-2315">
          <path d="M9 17V5l9-2v12" stroke="#F59E0B" strokeWidth="2" fill="none" strokeLinecap="round" data-id="element-2316" />
          <circle cx="7" cy="17" r="2.4" data-id="element-2317" />
          <circle cx="16" cy="15" r="2.4" data-id="element-2318" />
        </motion.svg>}

      {/* L4 — energetic sparkle */}
      {!isCompact && stage.level === 4 && <motion.svg aria-hidden viewBox="0 0 24 24" className="absolute top-0 right-0 w-1/4 h-1/4 pointer-events-none" animate={shouldAnimate ? {
      rotate: [0, 90, 0],
      scale: [0.8, 1.15, 0.8]
    } : undefined} transition={{
      repeat: Infinity,
      duration: 2.4,
      ease: 'easeInOut'
    }} style={{
      transformOrigin: 'center'
    }} data-id="element-2319">
          <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8z" fill="#FDE047" data-id="element-2320" />
        </motion.svg>}
    </motion.div>;
}